import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, FileText, Image, File, Loader2 } from 'lucide-react';

/**
 * Reusable FileUpload component for handling file uploads across the application.
 *
 * Props:
 * - files: Array of uploaded files (for controlled component mode)
 * - onFilesChange: Callback when files change (receives updated files array)
 * - uploadEndpoint: API endpoint for immediate upload (if not provided, files are stored locally)
 * - module: Module name for file metadata (e.g., 'ssem', 'incident')
 * - referenceId: Reference ID for linking files to a record
 * - description: Description for uploaded files
 * - tags: Tags for uploaded files
 * - multiple: Allow multiple file uploads (default: false)
 * - accept: Accepted file types (default: images only)
 * - maxSizeMB: Maximum file size in MB (default: 10)
 * - allowedTypes: Array of allowed file extensions (default based on accept prop)
 * - label: Label text for the upload area
 * - showSizeLimit: Whether to show the size limit message (default: true)
 * - className: Additional CSS classes for the container
 * - disabled: Disable the upload functionality
 * - onError: Callback for error handling
 * - onUploadStart: Callback when upload starts
 * - onUploadComplete: Callback when upload completes
 */

const FileUpload = ({
  files = [],
  onFilesChange,
  uploadEndpoint,
  module = '',
  referenceId = '',
  description = '',
  tags = '',
  multiple = false,
  accept = 'image/*',
  maxSizeMB = 10,
  allowedTypes = null,
  label = 'Upload File',
  showSizeLimit = true,
  className = '',
  disabled = false,
  onError,
  onUploadStart,
  onUploadComplete,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  // Default allowed types based on accept prop
  const getDefaultAllowedTypes = () => {
    if (accept === 'image/*') {
      return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    }
    if (accept.includes('pdf') || accept.includes('document')) {
      return ['.pdf', '.docx', '.xlsx', '.pptx', '.doc', '.xls'];
    }
    // Full list for general file upload
    return ['.pdf', '.docx', '.xlsx', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.xls'];
  };

  const effectiveAllowedTypes = allowedTypes || getDefaultAllowedTypes();
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const getFileIcon = (contentType, filename) => {
    const isImage = contentType?.startsWith('image/') ||
      /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filename);
    const isPdf = contentType?.includes('pdf') || /\.pdf$/i.test(filename);

    if (isImage) {
      return <Image className="w-4 h-4 text-green-600" />;
    } else if (isPdf) {
      return <FileText className="w-4 h-4 text-red-600" />;
    } else {
      return <File className="w-4 h-4 text-blue-600" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getAllowedTypesDisplay = () => {
    if (accept === 'image/*') {
      return 'Images (JPG, PNG, GIF)';
    }
    const types = effectiveAllowedTypes.map(t => t.replace('.', '').toUpperCase());
    if (types.length > 5) {
      return `${types.slice(0, 5).join(', ')}...`;
    }
    return types.join(', ');
  };

  const validateFile = (file) => {
    // Validate file type
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!effectiveAllowedTypes.includes(fileExt)) {
      return `File type ${fileExt} not allowed. Allowed: ${effectiveAllowedTypes.join(', ')}`;
    }

    // Validate file size
    if (file.size > maxSizeBytes) {
      return `File "${file.name}" exceeds ${maxSizeMB}MB limit`;
    }

    return null;
  };

  const handleFileSelect = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const filesToProcess = multiple ? Array.from(selectedFiles) : [selectedFiles[0]];
    const token = localStorage.getItem('token');

    setUploading(true);
    onUploadStart?.();

    const newFiles = [];
    const errors = [];

    for (const file of filesToProcess) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
        continue;
      }

      // If uploadEndpoint is provided, upload immediately
      if (uploadEndpoint) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          if (module) formData.append('module', module);
          if (referenceId) formData.append('reference_id', referenceId);
          if (description) formData.append('description', description);
          if (tags) formData.append('tags', tags);

          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}${uploadEndpoint}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          newFiles.push({
            id: response.data.id,
            filename: response.data.original_filename || response.data.filename || file.name,
            file_size: response.data.file_size || file.size,
            content_type: response.data.content_type || file.type,
            uploaded: true
          });
        } catch (error) {
          console.error('File upload error:', error);
          errors.push(`Failed to upload "${file.name}": ${error.response?.data?.detail || 'Upload failed'}`);
        }
      } else {
        // Store file locally for later upload (deferred upload mode)
        // Create a preview URL for images
        let preview = null;
        if (file.type.startsWith('image/')) {
          preview = URL.createObjectURL(file);
        }

        newFiles.push({
          id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          filename: file.name,
          file_size: file.size,
          content_type: file.type,
          file: file, // Store the actual file for later upload
          preview: preview,
          uploaded: false
        });
      }
    }

    // Report errors
    if (errors.length > 0) {
      onError?.(errors);
    }

    // Update files
    const updatedFiles = multiple ? [...files, ...newFiles] : newFiles;
    onFilesChange?.(updatedFiles);

    setUploading(false);
    onUploadComplete?.(newFiles);

    // Clear the input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const removeFile = async (fileToRemove) => {
    // If the file was uploaded to server, delete it
    if (fileToRemove.uploaded && fileToRemove.id) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileToRemove.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Error removing file from server:', error);
        onError?.([`Failed to remove file: ${error.response?.data?.detail || 'Delete failed'}`]);
        return; // Don't remove from state if server delete failed
      }
    }

    // Revoke object URL if it exists
    if (fileToRemove.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    // Remove from state
    const updatedFiles = files.filter(f => f.id !== fileToRemove.id);
    onFilesChange?.(updatedFiles);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const handleInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className={`file-upload-component ${className}`}>
      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="mb-3 space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Show preview for images */}
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt="Preview"
                    className="w-10 h-10 object-cover rounded"
                  />
                ) : (
                  getFileIcon(file.content_type, file.filename)
                )}
                <span className="text-sm text-gray-700 truncate">{file.filename}</span>
                {file.file_size && (
                  <span className="text-xs text-gray-500">({formatFileSize(file.file_size)})</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeFile(file)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                title="Remove file"
                disabled={disabled}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {(multiple || files.length === 0) && (
        <div
          className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer
            ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'bg-white hover:bg-gray-50'}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div className="text-center">
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="mt-2 text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="mx-auto h-10 w-10 text-gray-400" />
                <div className="mt-2">
                  <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                    {label}
                  </span>
                  <input
                    ref={inputRef}
                    type="file"
                    multiple={multiple}
                    onChange={handleInputChange}
                    className="hidden"
                    accept={accept}
                    disabled={disabled}
                  />
                  {showSizeLimit && (
                    <p className="text-xs text-gray-500 mt-1">
                      {getAllowedTypesDisplay()} (max {maxSizeMB}MB{multiple ? ' each' : ''})
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
